import {
  Box,
  Button,
  InputBase,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import CustomTable from "../../components/common-components/CustomTable";
import { AddIcon } from "../../assets/Icon/ArrowIcon";
import SearchIcon from "@mui/icons-material/Search";
import { useAppDispatch } from "../../stores/store";

import CommonPagination from "../../components/common-components/CommonPagination";
import ListLoadingNew from "../../components/common-components/ListLoadingNew";
import { useLocation, useNavigate } from "react-router-dom";
import NoOrdersFound from "../../components/common-components/NoOrdersFound";
import { CrossIcon } from "../../assets/Icon/ModalIcon";
import {
  CloseButton,
  CloseButtonWrapper,
} from "../../components/style/modalStyleLarge";
import { getAdminInvoiceList } from "../../stores/orders";
import { EyeIcon } from "../../assets/Icon/dashboredIcon";
import InvoiceDrawer from "../../components/common-components/InvoiceDrawer";
import { ContentCopy } from "@mui/icons-material";
import { setToast } from "../../stores/toast/toastslice";

const AdminInvoices = () => {
  const dispatch = useAppDispatch();

  const [total_records, setTotalRecords] = useState(0);
  const [data, setData] = useState<any>();
  const location = useLocation();
  const { state } = location;
  const navigate = useNavigate();
  const [page, setPage] = useState(state?.pageNo ? state?.pageNo : 1);
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState<any>("");
  const [limit] = useState(10);
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const [openDrawer, setOpenDrawer] = useState(false);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  useEffect(() => {
    fetchAgents();
  }, [page, limit, debouncedSearch]);

  const fetchAgents = async () => {
    try {
      setLoading(true);

      // Debounce delay
      await new Promise((resolve) => setTimeout(resolve, 200));

      const offset = (page - 1) * limit;

      const res = await dispatch<any>(
        getAdminInvoiceList({
          offset,
          limit,
          search: searchQuery.trim(),
        }),
      );

      if (res?.payload?.data) {
        setData(res?.payload?.data);
        setTotalRecords(res?.payload?.total_records);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };
  const handleOpen = () => navigate("createInvoice");

  const formatToTimeZone = (
    dateInput: string | Date,
    timeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone,
    locale: string = "en-US",
    options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "short",
      day: "2-digit",
    },
  ): string => {
    if (!dateInput) return "Invalid Date";

    const date = new Date(
      typeof dateInput === "string" && !dateInput.endsWith("Z")
        ? dateInput + "Z"
        : dateInput,
    );

    if (isNaN(date.getTime())) {
      return "Invalid Date";
    }

    return new Intl.DateTimeFormat(locale, {
      timeZone,
      ...options,
    }).format(date);
  };

  const copyToClipboard = async (text: string) => {
    if (!text) return false;

    // Modern API (HTTPS required)
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        dispatch<any>(
          setToast({
            message: "Payment URL copied!",
            type: "success",
            title: "Success",
          }),
        );
      } catch (err) {
        console.error("Clipboard API failed, falling back...", err);
      }
    }
  };

  return (
    <StyledBox sx={{ paddingY: "16px" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "20px",
          padding: "20px",
          "@media (min-width: 600px)": {
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          },
        }}
      >
        <Title>Invoices</Title>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            "@media (min-width: 600px)": {
              flexDirection: "row",
              alignItems: "center",
            },
          }}
        >
          <SearchWrapper>
            <SearchIcon />
            <StyledInputBase
              placeholder="Search here..."
              inputProps={{ "aria-label": "search" }}
              value={searchQuery}
              onChange={handleSearchChange}
            />
            {searchQuery && (
              <CloseButtonWrapper>
                <CloseButton
                  onClick={() => {
                    setSearchQuery("");
                  }}
                  sx={{
                    cursor: "pointer",
                    display: "inline-flex",
                    color: "red",
                    position: "absolute",
                    right: "1px",
                    top: "6.5px",
                  }}
                >
                  <CrossIcon />
                </CloseButton>
              </CloseButtonWrapper>
            )}
          </SearchWrapper>

          <StyledButton
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpen}
          >
            Create Invoice
          </StyledButton>
        </Box>
      </Box>

      <CustomTable
        headings={[
          "Sr. No",
          "Client Name",
          "Email",
          "Subscription Type",
          "Amount",
          "Status",
          "Created",
          "",
        ]}
      >
        <>
          {loading && (
            <TableRow>
              <TableCell
                colSpan={7}
                sx={{
                  padding: "16px 24px",
                }}
              >
                <ListLoadingNew />
              </TableCell>
            </TableRow>
          )}

          {!loading && data?.length === 0 && (
            <TableRow>
              <TableCell colSpan={7}>
                <NoOrdersFound
                  title="No Clients Found"
                  breakWords={["your"]}
                  subTitle="Try adding new client or modifying your search criteria."
                />
              </TableCell>
            </TableRow>
          )}
          {!loading &&
            data?.length > 0 &&
            data?.map((item: any, index: number) => (
              <TableRow>
                <TableCell
                  sx={{
                    padding: "16px 24px",
                  }}
                >
                  <Typography>{(page - 1) * limit + index + 1}</Typography>
                </TableCell>
                <TableCell
                  sx={{
                    padding: "16px 24px",
                  }}
                >
                  <Typography>
                    {item?.client?.first_name} {item?.client?.last_name}
                  </Typography>
                </TableCell>

                <TableCell
                  sx={{
                    padding: "16px 24px",

                    whiteSpace: "nowrap",
                  }}
                >
                  <Typography>{item?.client?.email || "-"}</Typography>
                </TableCell>
                <TableCell
                  sx={{
                    padding: "16px 24px",

                    whiteSpace: "nowrap",
                  }}
                >
                  <Typography>
                    {item?.subscription_type === "one_time" && "ONE TIME"}{" "}
                    {item?.subscription_type === "monthly" && "MONTHLY"}{" "}
                    {item?.subscription_type === "yearly" && "YEARLY"}
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{
                    padding: "16px 24px",
                    fontWeight: 500,
                  }}
                >
                  ${item?.total_amount}
                </TableCell>
                <TableCell
                  sx={{
                    padding: "16px 24px",

                    whiteSpace: "nowrap",
                  }}
                >
                  {item?.status_label === "Pending" && (
                    <div
                      style={{
                        background: "#ffd00020",
                        color: "#ffd000",
                        borderRadius: "8px",
                        fontSize: "14px",
                        paddingLeft: "12px",
                        paddingRight: "12px",
                        paddingTop: "10px",
                        paddingBottom: "10px",
                        fontWeight: 500,
                        textAlign: "center",
                      }}
                    >
                      {item?.status_label}
                    </div>
                  )}
                  {item?.status_label === "Paid" && (
                    <div
                      style={{
                        background: "#2977CC20",
                        color: "#2977CC",
                        borderRadius: "8px",
                        fontSize: "14px",
                        paddingLeft: "12px",
                        paddingRight: "12px",
                        paddingTop: "10px",
                        paddingBottom: "10px",
                        fontWeight: 500,
                        textAlign: "center",
                      }}
                    >
                      {item?.status_label}
                    </div>
                  )}
                  {item?.status_label === "Cancelled" && (
                    <div
                      style={{
                        background: "#ff000020",
                        color: "#ff0000",
                        borderRadius: "8px",
                        fontSize: "14px",
                        paddingLeft: "12px",
                        paddingRight: "12px",
                        paddingTop: "10px",
                        paddingBottom: "10px",
                        fontWeight: 500,
                        textAlign: "center",
                      }}
                    >
                      {item?.status_label}
                    </div>
                  )}
                </TableCell>
                <TableCell
                  sx={{
                    padding: "16px 24px",
                    fontWeight: 500,
                  }}
                >
                  {item?.created_at ? formatToTimeZone(item.created_at) : "-"}
                </TableCell>

                <TableCell>
                  <Box display="flex" alignItems="center" gap={3}>
                    {item?.id && (
                      <>
                        <Box
                          onClick={() => {
                            copyToClipboard(item?.payment_link);
                          }}
                          style={{ cursor: "pointer" }}
                        >
                          <ContentCopy
                            fontSize="medium"
                            style={{
                              color: "#a1a1a1",
                            }}
                          />
                        </Box>
                        <Box
                          onClick={() => {
                            setSelectedInvoice(item);
                            setOpenDrawer(true);
                          }}
                          style={{ cursor: "pointer" }}
                        >
                          <EyeIcon />
                        </Box>
                      </>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
        </>
      </CustomTable>

      {/* Pagination */}
      {Math.ceil(total_records / limit) > 1 && (
        <Box sx={{ display: "flex", justifyContent: "end", mt: 3 }}>
          <CommonPagination
            currentPage={page}
            totalPages={Math.ceil(total_records / limit)}
            onPageChange={handlePageChange}
          />
        </Box>
      )}

      {/** RIGHT DRAWER */}
      <InvoiceDrawer open={openDrawer} onClose={() => setOpenDrawer(false)}>
        <div>
          <div
            style={{
              fontWeight: 600,
              fontSize: 25,
              paddingTop: "0px",
              display: "flex",
              alignItems: "center",
              gap: 15,
            }}
          >
            <button
              style={{
                background: "transparent",
                border: "none",
                marginTop: 4,
                cursor: "pointer",
                marginLeft: -5,
              }}
              onClick={() => setOpenDrawer(false)}
            >
              {" "}
              <CrossIcon />
            </button>{" "}
            Invoice Details
          </div>
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "end",
              paddingRight: "20px",
              marginTop: "15px",
            }}
          >
            {selectedInvoice?.status_label === "Pending" && (
              <div
                style={{
                  background: "#ffd00020",
                  color: "#ffd000",
                  borderRadius: "18px",
                  fontSize: "16px",
                  paddingLeft: "20px",
                  paddingRight: "20px",
                  paddingTop: "6px",
                  paddingBottom: "6px",
                  fontWeight: 500,
                  textAlign: "center",
                }}
              >
                {selectedInvoice?.status_label}
              </div>
            )}
            {selectedInvoice?.status_label === "Paid" && (
              <div
                style={{
                  background: "#2977CC20",
                  color: "#2977CC",
                  borderRadius: "18px",
                  fontSize: "16px",
                  paddingLeft: "20px",
                  paddingRight: "20px",
                  paddingTop: "6px",
                  paddingBottom: "6px",
                  fontWeight: 500,
                  textAlign: "center",
                }}
              >
                {selectedInvoice?.status_label}
              </div>
            )}
            {selectedInvoice?.status_label === "Cancelled" && (
              <div
                style={{
                  background: "#ff000020",
                  color: "#ff0000",
                  borderRadius: "18px",
                  fontSize: "16px",
                  paddingLeft: "10px",
                  paddingRight: "10px",
                  paddingTop: "5px",
                  paddingBottom: "5px",
                  fontWeight: 500,
                  textAlign: "center",
                }}
              >
                {selectedInvoice?.status_label}
              </div>
            )}
          </div>
          <div
            style={{
              marginTop: "10px",
            }}
          >
            <div
              style={{
                fontWeight: 600,
              }}
            >
              Name:{" "}
              <span
                style={{
                  fontWeight: 400,
                }}
              >
                {selectedInvoice?.client?.first_name}{" "}
                {selectedInvoice?.client?.last_name}
              </span>
            </div>
            <div
              style={{
                fontWeight: 600,
                marginTop: "10px",
              }}
            >
              Email:{" "}
              <span
                style={{
                  fontWeight: 400,
                }}
              >
                {selectedInvoice?.client?.email}
              </span>
            </div>
            <div
              style={{
                fontWeight: 600,
                marginTop: "10px",
              }}
            >
              Type:{" "}
              <span
                style={{
                  fontWeight: 400,
                }}
              >
                {selectedInvoice?.subscription_type === "one_time" &&
                  "One Time"}
                {selectedInvoice?.subscription_type === "monthly" && "Monthly"}
                {selectedInvoice?.subscription_type === "yearly" && "Yearly"}
              </span>
            </div>

            <div
              style={{
                marginTop: 30,
              }}
            >
              <div
                style={{
                  fontWeight: 600,
                  fontSize: 16,
                  marginBottom: 10,
                }}
              >
                Line Items
              </div>

              <div
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 10,
                  overflow: "auto",
                }}
              >
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    overflowX: "scroll",
                  }}
                >
                  <thead>
                    <tr style={{ background: "#f7f8fb" }}>
                      <th
                        style={{
                          textAlign: "center",
                          padding: 10,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Product
                      </th>
                      <th
                        style={{
                          textAlign: "center",
                          padding: 10,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Description
                      </th>
                      <th
                        style={{
                          textAlign: "center",
                          padding: 10,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Qty
                      </th>
                      <th
                        style={{
                          textAlign: "center",
                          padding: 10,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Unit Cost
                      </th>
                      <th
                        style={{
                          textAlign: "center",
                          padding: 10,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Amount
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {selectedInvoice?.line_items?.map((item: any) => (
                      <tr
                        key={item.id}
                        style={{ borderBottom: "1px solid #e7e7e7" }}
                      >
                        <td style={{ textAlign: "center", padding: 10 }}>
                          {item.product_code === "BOOKKEEPING" && "Bookkeeping"}
                          {item.product_code === "INVOICING" && "Invoicing"}
                          {item.product_code === "TAX_FILING" && "Tax Filing"}
                          {item.product_code === "OPERATING_AGREEMENT" &&
                            "Operating Agreement"}
                          {item.product_code === "ITIN" && "ITIN"}
                          {item.product_code === "EIN" && "LLC Incorporation"}
                        </td>
                        <td style={{ textAlign: "center", padding: 10 }}>
                          {item.description || "--"}
                        </td>
                        <td style={{ textAlign: "center", padding: 10 }}>
                          {item.quantity}
                        </td>
                        <td style={{ textAlign: "center", padding: 10 }}>
                          ${item.unit_cost}
                        </td>
                        <td style={{ textAlign: "center", padding: 10 }}>
                          ${item.amount}
                        </td>
                      </tr>
                    ))}

                    {/* Total Row */}
                    <tr style={{ background: "#fafafa" }}>
                      <td
                        colSpan={4}
                        style={{
                          fontWeight: 600,
                          padding: 15,
                          paddingLeft: 40,
                        }}
                      >
                        Total
                      </td>
                      <td
                        style={{
                          fontWeight: 700,
                          textAlign: "center",
                          color: "#2977CC",
                        }}
                      >
                        ${selectedInvoice?.total_amount}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              {selectedInvoice?.notes && (
                <div
                  style={{
                    fontWeight: 600,
                    marginTop: "40px",
                  }}
                >
                  Notes:{" "}
                  <span
                    style={{
                      fontWeight: 400,
                    }}
                  >
                    {selectedInvoice?.notes}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </InvoiceDrawer>
    </StyledBox>
  );
};

export default AdminInvoices;

const StyledBox = styled(Box)({
  background: "#ffffff",
  boxShadow: "0px 2px 40px 0px #DDE3EA33",
  borderRadius: "10px",
});

const Title = styled(Typography)({
  color: "#01060B !important",
  fontSize: "20px !important",
  fontWeight: "600 !important",
});

const SearchWrapper = styled(Box)({
  display: "flex",
  alignItems: "center",
  background: "#F6F9FD",
  color: "#6B6B6B",
  borderRadius: "8px",
  padding: "5px 10px",
  position: "relative",
  "&:hover": {
    background: "#F6F9FD",
  },
});

const StyledButton = styled(Button)({
  width: "160px !important",
  whiteSpace: "nowrap",
  borderRadius: "6px !important",
  height: "40px !important",
});

const StyledInputBase = styled(InputBase)({
  minWidth: "300px",
  padding: "3px",
});
